import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { api, LOGO_WIDE } from "../lib/api";
import { FadeUp, SectionHead } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { ArtImage } from "../components/ArtImage";
import { PrintCard } from "./Home";
import { useSiteConfig } from "../hooks/useSiteConfig";
import { usePageTitle } from "../hooks/usePageTitle";

const MERCH = ["T-SHIRTS", "HATS", "MUGS", "MOUSE PADS", "BANDANAS"];

const CATEGORIES = [
  {
    key: "limited",
    title: "SIGNED + NUMBERED",
    note: "Individually numbered and hand-signed by the artist. When an edition is gone, it is gone.",
    empty: "NEW LIMITED EDITIONS DROPPING SOON",
  },
  {
    key: "open",
    title: "OPEN PRINTS",
    note: "Open edition prints — same artwork, same quality, no numbering. Produced to order.",
    empty: "OPEN PRINTS COMING SOON",
  },
  {
    key: "digital",
    title: "DIGITAL DOWNLOADS",
    note: "High-resolution digital files, delivered instantly. For personal display use.",
    empty: "DIGITAL DOWNLOADS COMING SOON",
  },
];

export default function Prints() {
  usePageTitle("The Print Shop — Signed Limited Editions, Open Prints, Digital Downloads");
  const [prints, setPrints] = useState([]);
  const [params, setParams] = useSearchParams();
  const config = useSiteConfig();
  const cat = params.get("cat");

  useEffect(() => {
    api.get("/prints").then((r) => setPrints(r.data)).catch(() => {});
  }, []);

  const byType = (t) => prints.filter((p) => (p.type || "limited") === t);
  const active = CATEGORIES.find((c) => c.key === cat);
  const shopify = config?.shopify_url;

  const openCat = (key) => {
    setParams(key ? { cat: key } : {});
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <div className="pb-20" data-testid="prints-page">
      <PageHero slug="piece-02" testId="store-hero">
        <FadeUp>
          <p className="text-[10px] font-bold tracking-[0.3em] text-smoke mb-3">COLLECTIBLE ARTWORK — NOT MERCH</p>
          <h1 className="font-display text-6xl sm:text-7xl tracking-wide leading-none" data-testid="prints-page-title">THE PRINT SHOP</h1>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-5 max-w-md">
          <p className="text-base text-bone/80 leading-relaxed" data-testid="prints-explainer">
            Three ways to own the work — every piece starts as an original by Kenny DePrince. Pick a lane.
          </p>
        </FadeUp>
      </PageHero>
      <div className="px-5 sm:px-8">

      {!active ? (
        <>
          {/* CATEGORY DOORS */}
          <div className="mt-10 space-y-4" data-testid="store-categories">
            {CATEGORIES.map((c, i) => {
              const items = byType(c.key);
              const cover = items[0];
              return (
                <FadeUp key={c.key} delay={i * 0.07}>
                  <button
                    onClick={() => openCat(c.key)}
                    data-testid={`cat-${c.key}`}
                    className="group relative w-full text-left border border-white/10 overflow-hidden min-h-[132px] sm:min-h-[180px] flex items-end"
                  >
                    {cover?.images && (
                      <ArtImage art={cover} size="md" sizes="(max-width: 640px) 100vw, 1200px"
                        className="absolute inset-0 w-full h-full object-cover object-center opacity-35 transition-all duration-500 group-hover:opacity-55 group-hover:scale-[1.02]"
                        testId={`cat-image-${c.key}`} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
                    <div className="relative p-5 sm:p-7 flex items-end justify-between w-full gap-4">
                      <div>
                        <span className="font-display text-pink text-base leading-none">{String(i + 1).padStart(2, "0")}</span>
                        <p className="font-display text-4xl sm:text-5xl tracking-wide leading-none mt-1">{c.title}</p>
                        <p className="mt-2 text-[10px] font-bold tracking-[0.25em] text-smoke">
                          {items.length > 0 ? `${items.length} ${items.length === 1 ? "PIECE" : "PIECES"}` : "COMING SOON"}
                        </p>
                      </div>
                      <span className="w-11 h-11 border border-white/25 flex items-center justify-center shrink-0 group-hover:border-pink group-hover:text-pink group-hover:translate-x-1 transition-all duration-200">
                        <ArrowRight size={18} />
                      </span>
                    </div>
                  </button>
                </FadeUp>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <FadeUp className="mt-6">
            <button onClick={() => openCat(null)} data-testid="back-to-categories"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-smoke hover:text-pink transition-colors duration-200 min-h-[44px]">
              <ArrowLeft size={14} /> ALL CATEGORIES
            </button>
          </FadeUp>
          <section className="mt-4" data-testid={`section-${active.key}`}>
            <SectionHead index={String(CATEGORIES.indexOf(active) + 1).padStart(2, "0")} title={active.title} />
            <FadeUp className="mt-4 max-w-md">
              <p className="text-sm text-smoke leading-relaxed">{active.note}</p>
            </FadeUp>
            {byType(active.key).length > 0 ? (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {byType(active.key).map((p, i) => (
                  <FadeUp key={p.id} delay={i * 0.06}>
                    <PrintCard print={p} index={i} />
                  </FadeUp>
                ))}
              </div>
            ) : (
              <FadeUp className="mt-8 border border-dashed border-white/15 p-8 text-center">
                <p className="text-xs tracking-[0.25em] text-smoke" data-testid={`section-${active.key}-empty`}>{active.empty}</p>
              </FadeUp>
            )}
          </section>
        </>
      )}

      {/* MERCH / SHOPIFY */}
      {!active && (
        <section className="mt-16 border-t border-white/10 pt-12" data-testid="merch-section">
          <SectionHead index="04" title="ARTILLERY KDP GOODS" />
          <FadeUp className="mt-4 max-w-md">
            <p className="text-sm text-smoke leading-relaxed">
              The logo on the everyday stuff — apparel and goods printed through the official ARTILLERY KDP store.
            </p>
          </FadeUp>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {MERCH.map((m, i) => {
              const inner = (
                <>
                  <img src={LOGO_WIDE} alt="ARTILLERY KDP logo" className="w-4/5 h-auto opacity-80 group-hover:opacity-100 transition-opacity duration-200" draggable={false} />
                  <span className="mt-4 text-[10px] font-extrabold tracking-[0.25em] text-bone/80">{m}</span>
                  <span className={`mt-1 text-[9px] tracking-[0.2em] ${shopify ? "text-pink" : "text-smoke"}`}>
                    {shopify ? "SHOP NOW" : "COMING SOON"}
                  </span>
                </>
              );
              const cls = "group border border-white/10 bg-charcoal/40 aspect-square flex flex-col items-center justify-center p-4 hover:border-pink transition-colors duration-200";
              return (
                <FadeUp key={m} delay={i * 0.05}>
                  {shopify ? (
                    <a href={shopify} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`merch-${m.toLowerCase().replace(/\s/g, "-")}`}>{inner}</a>
                  ) : (
                    <div className={cls} data-testid={`merch-${m.toLowerCase().replace(/\s/g, "-")}`}>{inner}</div>
                  )}
                </FadeUp>
              );
            })}
          </div>
          {shopify && (
            <FadeUp className="mt-6">
              <a href={shopify} target="_blank" rel="noopener noreferrer" data-testid="merch-shop-link"
                className="h-12 px-7 inline-flex items-center gap-2 bg-bone text-ink text-sm font-extrabold tracking-[0.18em] hover:bg-pink hover:text-white transition-colors duration-200">
                VISIT THE ARTILLERY KDP STORE <ArrowUpRight size={16} />
              </a>
            </FadeUp>
          )}
        </section>
      )}
      </div>
    </div>
  );
}
